import { env } from "@/constants/env";
import { reportRevenueCatError } from "@/utils/subscription/subscription-status-monitoring";
import { Capacitor } from "@capacitor/core";
import { refreshRevenueCatMetaAttribution, withRevenueCatCustomerChange } from "./meta-attribution";
import {
  LOG_LEVEL,
  Purchases,
  PURCHASES_ERROR_CODE,
  PurchasesPackage,
  type CustomerInfo,
  type PurchasesError,
} from "@revenuecat/purchases-capacitor";

let revenueCatConfigured: boolean = false;
let revenueCatInitializing: Promise<void> | null = null;
let revenueCatLoggedInUserId: string | null = null;

const normalizeKey = (value: string | undefined) => {
  if (!value) {
    return undefined;
  }
  const normalized = value.trim();
  if (!normalized || normalized === "undefined" || normalized === "null") {
    return undefined;
  }
  return normalized;
};

const isValidRevenueCatPublicKey = (platform: "ios" | "android", key: string) => {
  if (platform === "ios") {
    return key.startsWith("appl_");
  }
  return key.startsWith("goog_");
};

const isPurchasesError = (error: unknown): error is PurchasesError => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const candidate = error as { code?: unknown };
  return typeof candidate.code === "string";
};

export const initRevenueCat = async (isEnabled: boolean) => {
  if (!isEnabled) {
    console.info("[RevenueCat] Skipping initialization: subscription feature is disabled");
    return;
  }

  const platform = Capacitor.getPlatform();

  console.info(
    "[RevenueCat] Initializing RevenueCat",
    platform,
    env.VITE_IOS_REVENUE_CAT_PUBLIC_KEY,
    env.VITE_ANDROID_REVENUE_CAT_PUBLIC_KEY,
  );

  if (platform !== "ios" && platform !== "android") {
    return;
  }

  if (revenueCatConfigured) {
    return;
  }

  if (revenueCatInitializing) {
    await revenueCatInitializing;
    return;
  }

  revenueCatInitializing = (async () => {
    try {
      // Verbose SDK logs (offerings / purchase / eligibility) in non-prod to aid
      // device QA — set before configure so configuration logs are captured.
      // Visible in `adb logcat` (Android) / Xcode console (iOS).
      if (env.VITE_ENVIRONMENT !== "production") {
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      }

      if (platform === "ios") {
        const iosKey = normalizeKey(env.VITE_IOS_REVENUE_CAT_PUBLIC_KEY);
        if (!iosKey) {
          console.warn(
            "[RevenueCat] Skipping iOS configuration: missing VITE_IOS_REVENUE_CAT_PUBLIC_KEY",
          );
          return;
        }
        if (!isValidRevenueCatPublicKey("ios", iosKey)) {
          console.error("[RevenueCat] Skipping iOS configuration: invalid public key format");
          return;
        }
        console.info("[RevenueCat] Configuring iOS", iosKey);
        await Purchases.configure({
          apiKey: iosKey,
        });
        revenueCatConfigured = true;
        return;
      }

      const androidKey = normalizeKey(env.VITE_ANDROID_REVENUE_CAT_PUBLIC_KEY);
      if (!androidKey) {
        console.warn(
          "[RevenueCat] Skipping Android configuration: missing VITE_ANDROID_REVENUE_CAT_PUBLIC_KEY",
        );
        return;
      }
      if (!isValidRevenueCatPublicKey("android", androidKey)) {
        console.error("[RevenueCat] Skipping Android configuration: invalid public key format");
        return;
      }

      console.info("[RevenueCat] Configuring Android", androidKey);
      await Purchases.configure({
        apiKey: androidKey,
      });
      revenueCatConfigured = true;
    } catch (error) {
      console.warn("[RevenueCat] Failed to initialize", error);
    } finally {
      revenueCatInitializing = null;
    }
  })();

  await revenueCatInitializing;
  if (revenueCatConfigured) void refreshRevenueCatMetaAttribution();
};

export const refreshRevenueCatAttribution = async () => {
  if (revenueCatConfigured) await refreshRevenueCatMetaAttribution();
};

export const logInRevenueCat = async (userId: string, isEnabled: boolean) => {
  const normalizedUserId = userId.trim();
  if (!normalizedUserId) {
    return;
  }

  if (!isEnabled) {
    return;
  }

  if (Capacitor.getPlatform() === "ios" || Capacitor.getPlatform() === "android") {
    if (revenueCatLoggedInUserId === normalizedUserId) {
      return;
    }

    try {
      await initRevenueCat(isEnabled);
      if (!revenueCatConfigured) {
        return;
      }

      await withRevenueCatCustomerChange(async () => {
        await Purchases.logIn({ appUserID: normalizedUserId });
        revenueCatLoggedInUserId = normalizedUserId;
      });
      void refreshRevenueCatMetaAttribution();
    } catch (error) {
      console.warn("[RevenueCat] Failed to log in", error);
    }
  }
};

export const getOfferings = async (isEnabled: boolean) => {
  if (!isEnabled) {
    return null;
  }

  const platform = Capacitor.getPlatform();
  if (platform !== "ios" && platform !== "android") {
    return null;
  }

  try {
    // Wait for configuration before asking for offerings. Without this, the
    // paywall (which mounts right after onboarding) can call this before
    // `Purchases.configure()` has resolved, and the SDK throws ConfigurationError.
    await initRevenueCat(isEnabled);
    if (!revenueCatConfigured) {
      return null;
    }
    return await Purchases.getOfferings();
  } catch (error) {
    console.warn("[RevenueCat] Failed to get offerings", error);
    reportRevenueCatError("offerings", error);
    return null;
  }
};

export const getCustomerInfo = async (isEnabled: boolean): Promise<CustomerInfo | null> => {
  if (!isEnabled) {
    return null;
  }

  const platform = Capacitor.getPlatform();
  if (platform !== "ios" && platform !== "android") {
    return null;
  }

  try {
    await initRevenueCat(isEnabled);
    if (!revenueCatConfigured) {
      return null;
    }
    const info = await Purchases.getCustomerInfo();
    // Some environments return `{ customerInfo: CustomerInfo }`, normalize to `CustomerInfo`.
    const normalized =
      info && typeof info === "object" && "customerInfo" in info
        ? (info as { customerInfo: CustomerInfo }).customerInfo
        : (info as CustomerInfo);
    return normalized;
  } catch (error) {
    console.warn("[RevenueCat] Failed to get customer info", error);
    reportRevenueCatError("customer_info", error);
    return null;
  }
};

export const invalidateCustomerInfoCache = async (isEnabled: boolean) => {
  if (!isEnabled) {
    return;
  }

  const platform = Capacitor.getPlatform();
  if (platform !== "ios" && platform !== "android") {
    return;
  }

  try {
    await Purchases.invalidateCustomerInfoCache();
  } catch (error) {
    console.warn("[RevenueCat] Failed to invalidate customer info cache", error);
  }
};

export const isPurchaseCancelledByUser = (error: unknown): boolean =>
  isPurchasesError(error) && error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR;

export const purchasePackage = async (pkg: PurchasesPackage, isEnabled: boolean) => {
  if (!isEnabled) {
    throw new Error("[RevenueCat] purchasePackage is disabled by subscription feature flag");
  }

  const platform = Capacitor.getPlatform();
  if (platform !== "ios" && platform !== "android") {
    throw new Error("[RevenueCat] purchasePackage is only available on iOS/Android");
  }

  try {
    await refreshRevenueCatAttribution();
    return await Purchases.purchasePackage({ aPackage: pkg });
  } catch (error) {
    if (isPurchasesError(error) && error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
      console.warn("[RevenueCat] Purchase cancelled by user", error);
    } else {
      console.warn("[RevenueCat] Unexpected purchase error", error);
    }
    throw error;
  }
};
